import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { AuthService } from '@core/services/auth.service';
import { ToastService } from '@core/services/toast.service';
import { getModulesCourseById, IModulesCourseDefinition } from '../../data/course-modules.data';
import { ModuleFlowService } from '../../services/module-flow.service';
import { ModuleProgressService } from '../../services/module-progress.service';
import { CertificateService } from '../../services/certificate.service';
import { AccessibleConfirmService } from '../../services/accessible-confirm.service';

@Component({
  selector: 'prism-course-modules-detail',
  templateUrl: './course-modules-detail.component.html',
  styleUrl: './course-modules-detail.component.scss'
})
export class CourseModulesDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private flow = inject(ModuleFlowService);
  private progress = inject(ModuleProgressService);
  private certificateService = inject(CertificateService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private confirmService = inject(AccessibleConfirmService);
  private cdr = inject(ChangeDetectorRef);

  course?: IModulesCourseDefinition;
  courseId = 0;
  user = this.authService.getCurrentUser();
  earnedCertificate = false;
  viewCourseNoticeVisible = false;

  ngOnInit(): void {
    this.flow.reset();
    this.route.params.subscribe(params => {
      this.courseId = +params['courseId'];
      this.course = getModulesCourseById(this.courseId);

      // Load progress and certificate status
      this.progress.loadCourseProgress(this.courseId).subscribe(() => {
        this.cdr.markForCheck();
      });
      this.certificateService.hasCertificate(this.courseId).subscribe(earned => {
        this.earnedCertificate = earned;
        this.cdr.markForCheck();
      });
    });
  }

  get hasCertificate(): boolean {
    return this.earnedCertificate;
  }

  get allModulesComplete(): boolean {
    if (!this.course) return false;
    const moduleIds = this.course.modules.map(m => m.id);
    return this.progress.isCourseComplete(this.courseId, moduleIds);
  }

  // Completed (certificate) or Reviewed (all modules done, no certificate).
  get isViewOnly(): boolean {
    return this.hasCertificate || this.allModulesComplete;
  }

  get btnText(): string {
    return this.isViewOnly ? 'View Course' : 'Start Course';
  }

  toggleStartCourse(): void {
    // Completed or reviewed: tell them it's view-only before opening it.
    if (this.isViewOnly) {
      this.viewCourseNoticeVisible = true;
      return;
    }

    this.openCourse();
  }

  openCourse(): void {
    this.viewCourseNoticeVisible = false;
    this.progress.markCourseStarted(this.courseId);
    this.flow.passPopup();
    window.location.href = `/course-modules/${this.courseId}/list`;
  }

  viewPrintCertificate(): void {
    if (!this.hasCertificate || !this.course) return;

    this.certificateService.downloadCertificate(this.courseId).subscribe(blob => {
      saveAs(blob, 'PRISM-Certificate.pdf');
      this.toastService.success('Certificate downloaded.');
    });
  }

  resetCourse(): void {
    if (!this.course) return;

    this.confirmService.confirm({
      nzTitle: 'Reset course progress?',
      nzContent: `This will clear all module progress and notebook entries for ${this.course.name}. This cannot be undone.`,
      nzOkText: 'Reset',
      nzCancelText: 'Cancel',
      nzIconType: '',
      nzClosable: false,
      nzOnOk: () => {
        const moduleIds = this.course!.modules.map(m => m.id);
        this.progress.resetCourse(this.courseId, moduleIds);
        this.earnedCertificate = false;
        this.toastService.success('Course progress has been reset.');
        this.cdr.markForCheck();
      }
    });
  }
}
